import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

import Wordmark from "@/components/site/Wordmark";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/services/authService";

export const AuthScreen = ({
    mode = "login",
    title,
    subtitle,
    submitLabel,
    altLabel,
    altTo,
}) => {
    const { signIn, signUp } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const next = params.get("next") || "/app";

    const [form, setForm] = useState({
        email: "",
        password: "",
        name: "",
        company: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const update = (field) => (e) =>
        setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            if (mode === "signup") {
                await signUp(form);
                toast.success("Account created.");
            } else {
                await signIn({ email: form.email, password: form.password });
                toast.success("Signed in.");
            }
            navigate(next, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid={`${mode}-page`}
            className="min-h-screen flex flex-col bg-[#f6f7f6]"
        >
            <div className="border-b border-[#d8dcdd] bg-[#0e1416]">
                <div className="mx-auto max-w-[1280px] px-6 lg:px-10 h-[64px] flex items-center justify-between">
                    <Link to="/" className="flex items-baseline">
                        <Wordmark variant="dark" size="text-[17px]" />
                    </Link>
                    <Link
                        to="/"
                        className="tc-mono text-[11px] text-[#8a9498] hover:text-[#e6ebec] transition-colors"
                    >
                        ← Back to site
                    </Link>
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="w-full max-w-[420px] bg-[#ffffff] border border-[#d8dcdd] p-8 lg:p-10">
                    <div className="tc-eyebrow text-[#01696f] mb-3">
                        {mode === "signup" ? "Create account" : "Sign in"}
                    </div>
                    <h1 className="text-[22px] font-medium tracking-tight text-[#0e1416] leading-[1.25]">
                        {title}
                    </h1>
                    <p className="mt-3 text-[13px] text-[#5a6669] leading-[1.6]">
                        {subtitle}
                    </p>

                    <form
                        data-testid={`${mode}-form`}
                        onSubmit={onSubmit}
                        className="mt-7 flex flex-col gap-5"
                        noValidate
                    >
                        {mode === "signup" && (
                            <>
                                <Field
                                    id="name"
                                    label="Name"
                                    value={form.name}
                                    onChange={update("name")}
                                    placeholder="Jane Engineer"
                                    required
                                    testid={`${mode}-input-name`}
                                />
                                <Field
                                    id="company"
                                    label="Company"
                                    value={form.company}
                                    onChange={update("company")}
                                    placeholder="Firm or contractor"
                                    testid={`${mode}-input-company`}
                                />
                            </>
                        )}
                        <Field
                            id="email"
                            type="email"
                            label="Work email"
                            value={form.email}
                            onChange={update("email")}
                            placeholder="you@firm.com"
                            required
                            testid={`${mode}-input-email`}
                        />
                        <Field
                            id="password"
                            type="password"
                            label="Password"
                            value={form.password}
                            onChange={update("password")}
                            placeholder={mode === "signup" ? "Min 8 characters" : "••••••••"}
                            required
                            testid={`${mode}-input-password`}
                        />

                        {error && (
                            <div
                                data-testid={`${mode}-error`}
                                className="tc-mono text-[11px] text-[#a13434] border-l-2 border-[#a13434] pl-3 py-1"
                            >
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid={`${mode}-submit`}
                            className="group inline-flex items-center justify-between gap-3 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-60 disabled:cursor-not-allowed text-[#ffffff] px-4 py-3 text-[13px] font-medium transition-colors"
                        >
                            <span>{submitting ? "Working…" : submitLabel}</span>
                            <ArrowUpRight
                                size={14}
                                strokeWidth={1.5}
                                className="transition-transform group-hover:translate-x-[1px] group-hover:-translate-y-[1px]"
                            />
                        </button>
                    </form>

                    <div className="mt-7 pt-5 border-t border-[#d8dcdd] flex items-baseline justify-between">
                        <span className="tc-mono text-[11px] text-[#5a6669]">
                            {altLabel}
                        </span>
                        <Link
                            to={altTo}
                            data-testid={`${mode}-alt-link`}
                            className="tc-mono text-[12px] text-[#01696f] hover:text-[#015a60] transition-colors"
                        >
                            {mode === "signup" ? "Sign in" : "Create account"} →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Field = ({
    id,
    label,
    type = "text",
    value,
    onChange,
    placeholder,
    required,
    testid,
}) => (
    <div className="flex flex-col gap-2">
        <label
            htmlFor={id}
            className="tc-mono text-[10px] text-[#5a6669] tracking-wider uppercase"
        >
            {label}
            {required && <span className="text-[#01696f]"> *</span>}
        </label>
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            data-testid={testid}
            className="w-full bg-transparent border-0 border-b border-[#d8dcdd] focus:border-[#01696f] outline-none px-0 py-2 text-[14px] text-[#0e1416] placeholder:text-[#8a9498] transition-colors"
        />
    </div>
);

export const Login = () => (
    <AuthScreen
        mode="login"
        title="Sign in to Concrete Control."
        subtitle="Access your projects, calibrations, and monitoring."
        submitLabel="Sign in"
        altLabel="No account yet?"
        altTo="/signup"
    />
);

export const Signup = () => (
    <AuthScreen
        mode="signup"
        title="Create your Concrete Control account."
        subtitle="You'll start on the Demo tier — evaluate the suite indefinitely, upgrade when ready."
        submitLabel="Create account"
        altLabel="Already have an account?"
        altTo="/login"
    />
);
